import React, { useEffect } from 'react';
import useMedicalProfileFiles from '../../../hooks/api/useMedicalProfileFiles';
import FileFolderPath from '../FileFolderPath';
import GridDisplay, { KeyedNode } from '../../v2BaseComponents/GridDisplay';
import PressableDynamicOpacity from '../../v2BaseComponents/PressableDynamicOpacity';
import { APITypes } from '../../../primary-record-types';
import { getIntl } from '../../../utils/intl';
import Stack, { ContentStack } from '../../v2BaseComponents/Stack';
import { PromptActions } from '../../v2BaseComponents/Prompt';
import Icon from '../../v2BaseComponents/Icon';
import Text from '../../v2BaseComponents/Text';
import SkeletonLoadingContainer from '../../v2BaseComponents/SkeletonLoadingContainer';
import { getSpacing } from '../../../utils/spacing';
import { PRScrollView } from '../Scroll';

const intl = getIntl('FolderSelections', {
    selectFolder: 'Move to "{folderName}"',
    rootFolderName: 'Home',
});

export type FolderSelectionsOwnProps = {
    medicalProfileId: string;
    startFolderId: string;
    onSelectFolder: (folderId: string) => void;
    onClose: () => void;
    getIsDisabled?: (folderId: string) => boolean;
};

export type FolderSelectionsProps = FolderSelectionsOwnProps;

const ITEM_WIDTH = 80;

const FolderSelections: React.FC<FolderSelectionsProps> = ({
    startFolderId,
    onSelectFolder,
    onClose,
    medicalProfileId,
    getIsDisabled,
}) => {
    const [folderId, setFolderId] = React.useState(startFolderId);
    useEffect(() => {
        setFolderId(startFolderId);
    }, [startFolderId]);
    const { isLoadingFolderMetadata, folderMetadata } = useMedicalProfileFiles({
        medicalProfileId,
        folderId,
    });

    const folderName =
        folderMetadata?.targetFolder?.isRoot === false
            ? folderMetadata?.targetFolder?.folder?.folderName
            : intl('rootFolderName');

    const renderFolder = (
        folderMetadata: APITypes.medicalProfileFilesService.FolderWithDescendantFileCount,
    ): KeyedNode => ({
        gridItemKey: folderMetadata.medicalProfileFileFolderId,
        node: (
            <SkeletonLoadingContainer isLoading={isLoadingFolderMetadata}>
                <PressableDynamicOpacity
                    onPress={() => setFolderId(folderMetadata.medicalProfileFileFolderId)}
                    style={{
                        width: ITEM_WIDTH,
                    }}
                    testID={`folder-selection-folder-${folderMetadata.folderName}`}
                    isDisabled={
                        (getIsDisabled ? getIsDisabled(folderMetadata.medicalProfileFileFolderId) : false) ||
                        isLoadingFolderMetadata
                    }
                >
                    <Stack vertical align="center">
                        <Icon name="Folder" size="folder-picker-icon" />
                        <Text variant="caption-1" alignText="center">
                            {folderMetadata.folderName}
                        </Text>
                    </Stack>
                </PressableDynamicOpacity>
            </SkeletonLoadingContainer>
        ),
    });

    return (
        <ContentStack testID="select-folder-container">
            <SkeletonLoadingContainer isLoading={isLoadingFolderMetadata}>
                <FileFolderPath
                    onFolderSelected={setFolderId}
                    folderId={folderId}
                    medicalProfileId={medicalProfileId}
                    shortenPath
                />
            </SkeletonLoadingContainer>
            <Stack vertical fullWidth style={{ height: 200 }}>
                <PRScrollView style={{ width: '100%' }}>
                    <GridDisplay
                        items={
                            isLoadingFolderMetadata || !folderMetadata
                                ? []
                                : folderMetadata.descendants.medicalProfileFileFolders
                                      .sort((a, b) => {
                                          return a.folderName.localeCompare(b.folderName);
                                      })
                                      .map((folder) => renderFolder(folder))
                        }
                        itemWidth={ITEM_WIDTH}
                        padding={getSpacing(0.5)}
                        justifyRows="flex-start"
                    />
                </PRScrollView>
            </Stack>
            <PromptActions
                actions={[
                    {
                        title: intl('selectFolder', { folderName: isLoadingFolderMetadata ? '' : folderName }),
                        onPress: () => {
                            onSelectFolder(folderId);
                            onClose();
                        },
                        isDisabled: isLoadingFolderMetadata,
                        icon: 'CheckCircle',
                        testID: 'folder-selection-select-folder',
                    },
                ]}
            />
        </ContentStack>
    );
};

export default React.memo(FolderSelections);
